import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize,
  Minimize,
  X,
  Calendar,
  Camera,
  Tag,
  Info,
  Download,
  Share2,
  Check,
} from 'lucide-react';

export default function JubileeImageLightbox({ images = [], selectedIndex = 0, show, onHide, onSelectIndex }) {
  const [zoom, setZoom] = useState(1);
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);

  const currentImage = images[selectedIndex];

  const handlePrev = useCallback(() => {
    setZoom(1);
    if (selectedIndex > 0) {
      onSelectIndex(selectedIndex - 1);
    } else {
      onSelectIndex(images.length - 1); // Loop to end
    }
  }, [selectedIndex, images.length, onSelectIndex]);

  const handleNext = useCallback(() => {
    setZoom(1);
    if (selectedIndex < images.length - 1) {
      onSelectIndex(selectedIndex + 1);
    } else {
      onSelectIndex(0); // Loop to beginning
    }
  }, [selectedIndex, images.length, onSelectIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onHide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, handleNext, handlePrev, onHide]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.3, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.3, 0.5));
  const handleZoomReset = () => setZoom(1);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!currentImage) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      centered
      contentClassName="bg-black text-white border-0"
      dialogClassName="m-0 p-0"
    >
      {/* Top Header Controls Bar */}
      <div
        className="position-absolute top-0 start-0 end-0 p-3 d-flex justify-content-between align-items-center"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, transparent 100%)', zIndex: 1050 }}
      >
        <div className="d-flex align-items-center gap-3">
          <span className="badge bg-warning text-dark px-2 py-1 fw-bold">
            {selectedIndex + 1} / {images.length}
          </span>
          <span className="text-white fw-semibold small d-none d-md-inline text-truncate" style={{ maxWidth: '400px' }}>
            {currentImage.title}
          </span>
        </div>

        {/* Toolbar */}
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-dark bg-opacity-50 text-white rounded-circle p-2" onClick={handleZoomIn} title="Zoom In">
            <ZoomIn size={18} />
          </button>
          <button className="btn btn-sm btn-dark bg-opacity-50 text-white rounded-circle p-2" onClick={handleZoomOut} title="Zoom Out">
            <ZoomOut size={18} />
          </button>
          {zoom !== 1 && (
            <button className="btn btn-sm btn-dark bg-opacity-50 text-white rounded-circle p-2" onClick={handleZoomReset} title="Reset Zoom">
              <RotateCcw size={18} />
            </button>
          )}
          <button
            className={`btn btn-sm ${showInfoPanel ? 'btn-warning text-dark' : 'btn-dark bg-opacity-50 text-white'} rounded-circle p-2`}
            onClick={() => setShowInfoPanel(!showInfoPanel)}
            title="Image Info"
          >
            <Info size={18} />
          </button>
          <button className="btn btn-sm btn-dark bg-opacity-50 text-white rounded-circle p-2" onClick={toggleFullscreen} title="Fullscreen">
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
          <button className="btn btn-sm btn-danger rounded-circle p-2 ms-2" onClick={onHide} title="Close">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Main Lightbox Body */}
      <div className="d-flex h-100 w-100 position-relative overflow-hidden align-items-center justify-content-center">
        {/* Left Nav Arrow */}
        {images.length > 1 && (
          <button
            className="btn btn-link position-absolute start-0 text-white-50 hover:text-white p-3 z-3"
            style={{ fontSize: '2rem' }}
            onClick={handlePrev}
            title="Previous (Left Arrow)"
          >
            <ChevronLeft size={44} />
          </button>
        )}

        {/* Center Image Canvas */}
        <div
          className="d-flex align-items-center justify-content-center w-100 h-100 p-4"
          style={{ overflow: zoom > 1 ? 'auto' : 'hidden' }}
        >
          <img
            src={currentImage.fileUrl || currentImage.thumbnailUrl}
            alt={currentImage.title}
            className="img-fluid rounded shadow-2xl"
            style={{
              maxHeight: '85vh',
              maxWidth: '85vw',
              objectFit: 'contain',
              transform: `scale(${zoom})`,
              transition: 'transform 0.2s ease',
              cursor: zoom > 1 ? 'grab' : 'zoom-in',
            }}
            onClick={() => (zoom === 1 ? handleZoomIn() : handleZoomReset())}
          />
        </div>

        {/* Right Nav Arrow */}
        {images.length > 1 && (
          <button
            className="btn btn-link position-absolute end-0 text-white-50 hover:text-white p-3 z-3"
            style={{ fontSize: '2rem' }}
            onClick={handleNext}
            title="Next (Right Arrow)"
          >
            <ChevronRight size={44} />
          </button>
        )}

        {/* Collapsible Info Drawer / Panel */}
        {showInfoPanel && (
          <div
            className="position-absolute bottom-0 start-0 end-0 p-4"
            style={{
              background: 'linear-gradient(to top, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.8) 70%, transparent 100%)',
              zIndex: 1040,
              maxHeight: '40vh',
              overflowY: 'auto',
            }}
          >
            <div className="container" style={{ maxWidth: '900px' }}>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Badge bg="warning" text="dark" className="fw-bold">
                    Image Archive
                  </Badge>
                  {currentImage.year && (
                    <Badge bg="secondary" className="d-flex align-items-center gap-1">
                      <Calendar size={12} /> {currentImage.year}
                    </Badge>
                  )}
                  {currentImage.eventName && (
                    <Badge bg="success" className="bg-opacity-75">
                      {currentImage.eventName}
                    </Badge>
                  )}
                  {currentImage.categoryName && (
                    <Badge bg="info" className="bg-opacity-75">
                      {currentImage.categoryName}
                    </Badge>
                  )}
                </div>

                <div className="d-flex gap-2">
                  <Button variant="outline-light" size="sm" onClick={handleCopyLink} className="d-flex align-items-center gap-1 py-1 px-2 text-xs">
                    {copied ? <Check size={14} className="text-success" /> : <Share2 size={14} />}
                    <span>{copied ? 'Link Copied' : 'Share'}</span>
                  </Button>
                  <a
                    href={currentImage.fileUrl || currentImage.thumbnailUrl}
                    download={currentImage.fileName || 'archive-image.jpg'}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1 py-1 px-2 text-xs"
                  >
                    <Download size={14} /> Download
                  </a>
                </div>
              </div>

              <h5 className="fw-bold text-white mb-2">{currentImage.title}</h5>
              <p className="text-light text-opacity-75 small mb-2">{currentImage.description}</p>

              <div className="d-flex flex-wrap gap-3 small text-white-50">
                {currentImage.author && (
                  <div>
                    <Camera size={13} className="me-1 text-warning" />
                    <strong>Source/Photographer:</strong> {currentImage.author}
                  </div>
                )}
                {currentImage.person && (
                  <div>
                    <strong>Subject:</strong> {currentImage.person}
                  </div>
                )}
                {currentImage.source && (
                  <div>
                    <strong>Archive Location:</strong> {currentImage.source}
                  </div>
                )}
              </div>

              {currentImage.tags && currentImage.tags.length > 0 && (
                <div className="mt-2 d-flex flex-wrap gap-1 align-items-center">
                  <Tag size={12} className="text-warning" />
                  {currentImage.tags.map((t, idx) => (
                    <span key={idx} className="badge bg-dark bg-opacity-75 text-light border border-secondary border-opacity-50 px-2 py-1 text-xs">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
